/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import { STAKE_POOL_PROGRAM_ADDRESS } from '../programs';
import { getAccountMetaFactory, type ResolvedAccount } from '../shared';

export const ACCEPT_PROGRAM_AUTHORITY_DISCRIMINATOR = 11;

export function getAcceptProgramAuthorityDiscriminatorBytes() {
  return getU8Encoder().encode(ACCEPT_PROGRAM_AUTHORITY_DISCRIMINATOR);
}

export type AcceptProgramAuthorityInstruction<
  TProgram extends string = typeof STAKE_POOL_PROGRAM_ADDRESS,
  TAccountProgramAuthority extends string | AccountMeta<string> = string,
  TAccountPendingAuthority extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountProgramAuthority extends string
        ? WritableAccount<TAccountProgramAuthority>
        : TAccountProgramAuthority,
      TAccountPendingAuthority extends string
        ? ReadonlySignerAccount<TAccountPendingAuthority> &
            AccountSignerMeta<TAccountPendingAuthority>
        : TAccountPendingAuthority,
      ...TRemainingAccounts,
    ]
  >;

export type AcceptProgramAuthorityInstructionData = { discriminator: number };

export type AcceptProgramAuthorityInstructionDataArgs = {};

export function getAcceptProgramAuthorityInstructionDataEncoder(): FixedSizeEncoder<AcceptProgramAuthorityInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([['discriminator', getU8Encoder()]]),
    (value) => ({
      ...value,
      discriminator: ACCEPT_PROGRAM_AUTHORITY_DISCRIMINATOR,
    })
  );
}

export function getAcceptProgramAuthorityInstructionDataDecoder(): FixedSizeDecoder<AcceptProgramAuthorityInstructionData> {
  return getStructDecoder([['discriminator', getU8Decoder()]]);
}

export function getAcceptProgramAuthorityInstructionDataCodec(): FixedSizeCodec<
  AcceptProgramAuthorityInstructionDataArgs,
  AcceptProgramAuthorityInstructionData
> {
  return combineCodec(
    getAcceptProgramAuthorityInstructionDataEncoder(),
    getAcceptProgramAuthorityInstructionDataDecoder()
  );
}

export type AcceptProgramAuthorityInput<
  TAccountProgramAuthority extends string = string,
  TAccountPendingAuthority extends string = string,
> = {
  /** The program authority PDA */
  programAuthority: Address<TAccountProgramAuthority>;
  /** The pending authority accepting the transfer */
  pendingAuthority: TransactionSigner<TAccountPendingAuthority>;
};

export function getAcceptProgramAuthorityInstruction<
  TAccountProgramAuthority extends string,
  TAccountPendingAuthority extends string,
  TProgramAddress extends Address = typeof STAKE_POOL_PROGRAM_ADDRESS,
>(
  input: AcceptProgramAuthorityInput<
    TAccountProgramAuthority,
    TAccountPendingAuthority
  >,
  config?: { programAddress?: TProgramAddress }
): AcceptProgramAuthorityInstruction<
  TProgramAddress,
  TAccountProgramAuthority,
  TAccountPendingAuthority
> {
  // Program address.
  const programAddress = config?.programAddress ?? STAKE_POOL_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    programAuthority: {
      value: input.programAuthority ?? null,
      isWritable: true,
    },
    pendingAuthority: {
      value: input.pendingAuthority ?? null,
      isWritable: false,
    },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.programAuthority),
      getAccountMeta(accounts.pendingAuthority),
    ],
    data: getAcceptProgramAuthorityInstructionDataEncoder().encode({}),
    programAddress,
  } as AcceptProgramAuthorityInstruction<
    TProgramAddress,
    TAccountProgramAuthority,
    TAccountPendingAuthority
  >);
}

export type ParsedAcceptProgramAuthorityInstruction<
  TProgram extends string = typeof STAKE_POOL_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** The program authority PDA */
    programAuthority: TAccountMetas[0];
    /** The pending authority accepting the transfer */
    pendingAuthority: TAccountMetas[1];
  };
  data: AcceptProgramAuthorityInstructionData;
};

export function parseAcceptProgramAuthorityInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>
): ParsedAcceptProgramAuthorityInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    // TODO: Coded error.
    throw new Error('Not enough accounts');
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      programAuthority: getNextAccount(),
      pendingAuthority: getNextAccount(),
    },
    data: getAcceptProgramAuthorityInstructionDataDecoder().decode(
      instruction.data
    ),
  };
}
